import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { ShieldCheck, ExternalLink, Loader2, Info, Check } from "lucide-react";

export default function PolicyAckPrompt() {
  const { token, user, logout, refreshUser } = useApp();
  const [st, setSt] = useState(null);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dismissed, setDismissed] = useState(false);

  const isRunner = !!token && !!user && user.role === "user" && !user.demo;

  useEffect(() => {
    if (!isRunner) { setSt(null); return; }
    let live = true;
    client.get("/policy-ack/status").then((r) => { if (live) setSt(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [isRunner, user]);

  if (!isRunner || !st || !st.needs || dismissed) return null;

  const notify = st.mode === "notify";
  const allChecked = (st.policies || []).every((p) => checks[p.key]);

  const submit = async () => {
    setErr("");
    if (!notify && !allChecked) { setErr("Please tick each policy to confirm you have reviewed it."); return; }
    setBusy(true);
    try {
      await client.post("/policy-ack/accept");
      await refreshUser();
      setDismissed(true);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const PolicyLinks = () => (
    <div className="space-y-1.5">
      {(st.policies || []).map((p) => (
        <a key={p.key} href={p.href} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline"
          data-testid={`policy-ack-link-${p.key}`}>
          {p.title} <ExternalLink size={12} />
        </a>
      ))}
    </div>
  );

  // ---- Notify-only: dismissible bottom banner, does NOT block the app ----
  if (notify) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-[95] p-3 sm:p-4" data-testid="policy-ack-notice">
        <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border border-black/10 bg-white/95 p-4 shadow-2xl backdrop-blur-md dark:border-white/10 dark:bg-neutral-900/95 sm:flex-row sm:items-center">
          <Info size={20} className="shrink-0 text-[var(--primary)]" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">We've updated some policies</div>
            <p className="mt-0.5 text-xs opacity-70">{st.note || "Please take a moment to review the following updated policies."}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {(st.policies || []).map((p) => (
                <a key={p.key} href={p.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--primary)] hover:underline"
                  data-testid={`policy-notice-link-${p.key}`}>
                  {p.title} <ExternalLink size={11} />
                </a>
              ))}
            </div>
          </div>
          <button type="button" onClick={submit} disabled={busy} data-testid="policy-ack-dismiss"
            className="shrink-0 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--primary-hover)] disabled:opacity-60">
            {busy ? <Loader2 size={15} className="inline animate-spin" /> : "Got it"}
          </button>
        </div>
      </div>
    );
  }

  // ---- Accept mode: full-screen blocking prompt ----
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center overflow-auto p-4 sm:p-8"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} data-testid="policy-ack-gate">
      <div className="w-full max-w-lg rounded-xl border border-black/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-neutral-900">
        <div className="flex items-center gap-2 font-head text-2xl">
          <ShieldCheck size={24} className="text-[var(--primary)]" /> Updated policies
        </div>
        <p className="mt-2 text-sm opacity-75">
          {st.note || "We've made material changes to the policies below. Please review each one and confirm your acceptance to continue using the applications."}
        </p>

        <div className="mt-4 space-y-2">
          {(st.policies || []).map((p) => (
            <label key={p.key} className="flex items-start gap-2 rounded-lg border border-black/10 p-3 text-sm dark:border-white/10">
              <input type="checkbox" className="mt-0.5" checked={!!checks[p.key]}
                onChange={(e) => setChecks((c) => ({ ...c, [p.key]: e.target.checked }))}
                data-testid={`policy-ack-chk-${p.key}`} />
              <span className="flex-1">
                I have reviewed and accept the{" "}
                <a href={p.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-[var(--primary)] hover:underline"
                  data-testid={`policy-ack-link-${p.key}`}>
                  {p.title} <ExternalLink size={12} />
                </a>
              </span>
            </label>
          ))}
        </div>

        {err && <div className="mt-3 text-sm text-[#DA1E28]" data-testid="policy-ack-error">{err}</div>}

        <div className="mt-5 flex items-center justify-between">
          <button type="button" onClick={logout} className="text-sm opacity-70 hover:opacity-100" data-testid="policy-ack-signout">
            Sign out
          </button>
          <button type="button" onClick={submit} disabled={busy || !allChecked} data-testid="policy-ack-accept"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--primary-hover)] disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Accept &amp; continue
          </button>
        </div>
      </div>
    </div>
  );
}
